import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Container,
  Form,
  InputGroup,
  Pagination,
  Table,
} from 'react-bootstrap';
import { apiPrefix } from '../../config';
import { getAuthHeader } from '../../helpers/auth';
import { handleRequestError } from '../../helpers/requests';
import { CognitoUser } from '../../types';
import Toasts from '../common/Toasts';
import UserRow, { UserRowHead } from './UserRow';

const PAGE_SIZE = 20;

type FilterKey =
  | 'email'
  | 'custom:Company'
  | 'custom:Division'
  | 'custom:ActiveRegion';

const FILTER_KEYS: FilterKey[] = [
  'email',
  'custom:Company',
  'custom:Division',
  'custom:ActiveRegion',
];

const EMPTY_FILTERS: Record<FilterKey, string> = {
  email: '',
  'custom:Company': '',
  'custom:Division': '',
  'custom:ActiveRegion': '',
};

const getUserField = (user: CognitoUser, key: FilterKey): string => {
  if (key === 'email') return user.email ?? '';
  return user[key] ?? '';
};

const ViewUsers = () => {
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [developers, setDevelopers] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] =
    useState<Record<FilterKey, string>>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const fetchUsers = async () => {
    setLoading(true);
    const requestPath = `${apiPrefix}/cognitouser`;
    const response = await fetch(requestPath, {
      headers: await getAuthHeader(),
    });
    if (response.ok) {
      const data = await response.json();
      const users: CognitoUser[] = data.cognitoUsers;
      setUsers(users);
    } else {
      setUsers([]);
      handleRequestError(response);
    }
    setLoading(false);
  };

  const fetchDevelopers = async () => {
    const response = await fetch(`${apiPrefix}/developer`, {
      headers: await getAuthHeader(),
    });
    if (response.ok) {
      const data = await response.json();
      const names: string[] = (data.developers ?? [])
        .map((d: { developername: string }) => d.developername)
        .filter((n: string) => n && n.trim() !== '')
        .sort((a: string, b: string) => a.trim().localeCompare(b.trim()));
      setDevelopers(Array.from(new Set(names)));
    } else handleRequestError(response);
  };

  const fetchRegions = async () => {
    const response = await fetch(`${apiPrefix}/authority`, {
      headers: await getAuthHeader(),
    });
    if (response.ok) {
      const data = await response.json();
      const all: string[] = (data.councils ?? [])
        .map((c: { region: string }) => c.region)
        .filter((r: string) => r && r.trim() !== '');
      const unique = Array.from(new Set(all)).sort((a, b) =>
        a.trim().localeCompare(b.trim())
      );
      setRegions(unique);
    } else handleRequestError(response);
  };

  useEffect(() => {
    fetchUsers();
    fetchDevelopers();
    fetchRegions();
  }, []);

  const onUserRowChange = ({ user }: { user: CognitoUser }) => {
    const newUsers = users.map((u) =>
      u.Username === user.Username ? { ...u, ...user } : u
    );
    setUsers(newUsers);
    fetchUsers();
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) =>
      FILTER_KEYS.every((key) => {
        const query = filters[key].trim().toLowerCase();
        if (!query) return true;
        return getUserField(u, key).toLowerCase().includes(query);
      })
    );
  }, [users, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handleFilterChange =
    (key: FilterKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const clearFilter = (key: FilterKey) => () =>
    setFilters((prev) => ({ ...prev, [key]: '' }));

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items: JSX.Element[] = [];
    const windowSize = 2;
    const start = Math.max(1, currentPage - windowSize);
    const end = Math.min(totalPages, currentPage + windowSize);

    items.push(
      <Pagination.First
        key="first"
        onClick={() => setPage(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      />
    );
    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPage(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) items.push(<Pagination.Ellipsis key="el-start" disabled />);
    }
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1)
        items.push(<Pagination.Ellipsis key="el-end" disabled />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => setPage(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing {(currentPage - 1) * PAGE_SIZE + 1}–
          {Math.min(currentPage * PAGE_SIZE, filteredUsers.length)} of{' '}
          {filteredUsers.length}
        </small>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  const renderFilterCell = (key: FilterKey, placeholder: string) => (
    <th style={{ minWidth: 160 }}>
      <InputGroup size="sm">
        <Form.Control
          type="text"
          placeholder={`Search ${placeholder}`}
          value={filters[key]}
          onChange={handleFilterChange(key)}
        />
        {filters[key] && (
          <InputGroup.Text
            role="button"
            onClick={clearFilter(key)}
            title="Clear"
            style={{ cursor: 'pointer' }}
          >
            ×
          </InputGroup.Text>
        )}
      </InputGroup>
    </th>
  );

  return (
    <Container className="my-4" style={{ position: 'relative' }}>
      <Toasts info={info} error={error} setInfo={setInfo} setError={setError} />
      <div className="d-flex align-items-center py-3">
        <h3 className="fw-light mb-0">List of Users</h3>
        <span className="text-muted ms-3">
          {filteredUsers.length}
          {filteredUsers.length !== users.length && ` of ${users.length}`} user
          {filteredUsers.length === 1 ? '' : 's'}
        </span>
        {loading && (
          <div className="spinner-grow mx-3 text-info" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead className="table-light">
              <UserRowHead />
              <tr>
                {renderFilterCell('email', 'email')}
                {renderFilterCell('custom:Company', 'company')}
                {renderFilterCell('custom:Division', 'division')}
                {renderFilterCell('custom:ActiveRegion', 'region')}
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((user) => (
                <UserRow
                  key={user.Username}
                  user={user}
                  developers={developers}
                  regions={regions}
                  onChange={onUserRowChange}
                />
              ))}
              {!loading && pagedUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No users match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      {renderPagination()}
    </Container>
  );
};

export default ViewUsers;
